import * as Sentry from '@sentry/react';
import { useRouter } from 'next/router';

import { useTicketsIdByDatoEventSlug } from 'queries/tickets/useTicketsIdByDatoEventSlug';
import { AppSyncGraphQLError } from 'queries/types';
import { useCartStore, useCartStoreActions } from 'services/stores/cart';

import { AppSyncTicketsGraphQLError } from './types';

export const isNotFound = (
  error: AppSyncGraphQLError | AppSyncTicketsGraphQLError,
) =>
  !!error?.response?.errors?.find(
    (err) => err?.errorInfo?.statusCode === 404 || err.errorType === 'NotFound',
  );

export const intlIdForTicketingError = (
  page: 'cart' | 'coupon' | 'order',
  errors?: AppSyncTicketsGraphQLError,
) => {
  const intlBase = `tickets.${page}`;
  const errorCode =
    errors?.response?.errors?.[0]?.errorInfo?.response?.errorCode;

  // First try to return a specific error message
  if (!!errorCode) {
    switch (errorCode) {
      case 'inventory_not_enough_stock':
      case 'inventory-00000003':
      case 'inventory_out_of_stock':
      case 'inventory-00000002':
      case 'inventory_missing_channel_inventories':
      case 'inventory_missing_ticket_inventories':
        return `${intlBase}.inventory_not_enough_stock.label`;
      case 'sales_wrong_payment_method':
        return `${intlBase}.error.sales_wrong_payment_method`;
      case 'inventory_error':
        return `${intlBase}.inventory_error.label`;
      case 'CouponRecipientDoesNotMatch':
        return `${intlBase}.couponRecipientDoesNotMatch.label`;
      case 'CouponLimitReached':
        return `${intlBase}.couponLimitReached.label`;
      case 'sales-00000020':
        return `${intlBase}.error.apple_pay_insufficient_funds.label`;
    }
  }

  //Fallback for coupons
  if (page === 'coupon') {
    return `tickets.checkout.apply.coupon.error`;
  }

  if (!!errors) {
    // We have an error but no way to handle it so log it to Sentry
    Sentry.captureException(errors);
  }

  // If no specific error message is found, return a generic error message
  return `${intlBase}.generic.error.label`;
};

// A couple of helper hooks to get some typical returning data
// that is only used in the hooks files
export const useTicketingCartId = () => {
  const cartId = useCartStore((state) => state.ticketingCartId);
  const { setTicketingCartId } = useCartStoreActions();
  // Make typescript happy but this should never happen
  return {
    cartId: cartId ?? '',
    setCartId: setTicketingCartId,
  };
};

export const useTicketingEventId = () => {
  const { query } = useRouter();
  const slug = query.slug?.toString() ?? '';

  const { ticketsEventId, ticketsTimeslotId } = useTicketsIdByDatoEventSlug({
    slug,
  });

  return { eventId: ticketsEventId, timeslotId: ticketsTimeslotId };
};
